// AjaxOptionsMixin.js
// Provides AJAX option loading for Select/MultiSelect widgets.
// Expects field.params.ajax_url (string) and field.params.send_auth (boolean).
// Response must be [{value, text}, ...] — same format as static params.options.
//
// When send_auth is true:  uses this.$axios which carries Authorization: bearer
//   via the Vuex auth store plugin (auth/store/plugin.js).
// When send_auth is false: uses a plain axios instance with no headers, safe
//   for public or third-party endpoints where the token must not be sent.
import axios from 'axios';

export default {
  data() {
    return {
      ajaxOptions: [],
      ajaxLoading: false,
      ajaxError: null,
      _ajaxController: null,
    };
  },
  computed: {
    isAjaxMode() {
      return !!(this.field && this.field.params && this.field.params.ajax_url);
    },
    effectiveOptions() {
      const base = this.isAjaxMode
        ? this.ajaxOptions
        : (this.field && this.field.params && this.field.params.options) || [];

      // field_value is only present in Widget context (via FormWidgetMixin).
      const currentValue = this.field_value;
      const isCurrent = (opt) =>
        opt.value === currentValue || String(opt.value) === String(currentValue);

      // Hide disabled options unless they are the currently saved value —
      // keeps the list clean while still resolving stale selections on load.
      // Disabled options that survive the filter get "(removed)" appended so
      // the user understands why they cannot re-select the option.
      const filtered = base
        .filter((opt) => !opt.disabled || isCurrent(opt))
        .map((opt) => opt.disabled ? { ...opt, text: `${opt.text} (removed)` } : opt);

      // If the current value isn't in the list at all (e.g. deleted record),
      // prepend a disabled "(unknown)" sentinel so the saved value is visible.
      if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
        // Handle both single values and arrays (for MultiSelect)
        const currentValues = Array.isArray(currentValue)
          ? currentValue
          : [currentValue];
        const found = currentValues.every((val) =>
          filtered.some(
            (opt) => opt.value === val || String(opt.value) === String(val)
          )
        );

        if (!found) {
          // Add unknown entries for any missing values
          const missingValues = currentValues.filter(
            (val) =>
              !filtered.some(
                (opt) => opt.value === val || String(opt.value) === String(val)
              )
          );
          const unknownOptions = missingValues.map((val) => ({
            value: val,
            text: "(unknown)",
            disabled: true,
          }));
          return [...unknownOptions, ...filtered];
        }
      }

      return filtered;
    },
  },
  methods: {
    async fetchAjaxOptions(url, sendAuth) {
      if (!url) return;
      if (this._ajaxController) {
        this._ajaxController.abort();
      }
      this._ajaxController = new AbortController();
      this.ajaxLoading = true;
      this.ajaxError = null;

      // Use the app's axios instance (with Bearer token) or a plain instance
      // with no headers when calling public endpoints.
      const http = sendAuth ? this.$axios : axios.create();

      try {
        const response = await http.get(url, {
          signal: this._ajaxController.signal,
        });
        this.ajaxOptions = Array.isArray(response.data) ? response.data : [];
        this._ajaxController = null;
      } catch (err) {
        if (err.name !== 'AbortError' && err.code !== 'ERR_CANCELED') {
          this.ajaxError = err.response
            ? `${err.response.status} ${err.response.statusText}`
            : (err.message || 'Request failed');
          this._ajaxController = null;
        }
      } finally {
        this.ajaxLoading = false;
      }
    },
    _loadIfAjax() {
      if (this.isAjaxMode) {
        this.fetchAjaxOptions(this.field.params.ajax_url, this.field.params.send_auth);
      }
    },
  },
  mounted() {
    this._loadIfAjax();
  },
  watch: {
    'field.params.ajax_url'() {
      this._loadIfAjax();
    },
  },
};
